import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, useRef } from 'react';
import { CACHE_CONSTANTS } from '../../../../../constants/cache';
import { CoinWithSymbol, Transaction, TransactionType, TransactionTypeValue } from '../../../../../services/transaction.types';
import { useUserService } from '../../../../../services/user.service';
import { useTransactionService } from '../../../../../services/transaction.service';

const isValidDecimalInput = (value: string): boolean => {
    return value === '' || /^\d*,?\d*$/.test(value);
};

const formatDecimalInput = (rawValue: string): string => {
    if (rawValue.startsWith(',')) return '0' + rawValue;
    if (rawValue === '0' || rawValue.startsWith('0,')) return rawValue;
    return rawValue.replace(/^0+/, '');
};

const parseDecimalInput = (value: string): number => {
    const numValue = value.replace(',', '.');
    return numValue.endsWith('.') || numValue === '' ? 0 : parseFloat(numValue);
};

const extractValue = (value: string | null) => {
    return value?.startsWith('Create "') && value.endsWith('"') ? value.slice(8, -1) : value || '';
};

export const useTransactionCalculations = (
    formValues: Partial<Transaction>,
    handleChange: (field: keyof Transaction, value: any) => void,
    showLiquidityPool: boolean,
    transactionTypesWithTransfer: TransactionType[]
) => {
    const userService = useUserService();
    const transactionService = useTransactionService();
    const [isUpdatingPrices, setIsUpdatingPrices] = useState({ buy: false, sell: false, fee: false });
    const [inputValues, setInputValues] = useState<Record<string, string>>({});
    const prevTypeIdRef = useRef<TransactionTypeValue | undefined>();

    const buyEnabled = !!(formValues.buyCoin && formValues.buy && formValues.date);
    const sellEnabled = !!(formValues.sellCoin && formValues.sell && formValues.date);
    const feeEnabled = !!(formValues.feeCoin && formValues.date);

    // Handle fee coin selection based on transaction type and coin values
    useEffect(() => {
        const { buyCoin, sellCoin, fee, typeId, feeCoin: currentFeeCoin, buyTicker, sellTicker, feeTicker: currentFeeTicker } = formValues;
        const feeAmount = fee ?? 0;

        if (typeId === undefined) {
            return;
        }

        // Check if transaction type has changed
        if (prevTypeIdRef.current !== undefined && prevTypeIdRef.current !== typeId) {
            // Get the current and previous transaction types
            const currentType = transactionTypesWithTransfer.find(t => t.value === typeId);
            const prevType = transactionTypesWithTransfer.find(t => t.value === prevTypeIdRef.current);
            
            // Only reset fields if the transaction direction changes
            // This preserves fields when changing between similar types (e.g., from one incoming type to another)
            const currentIsIncoming = currentType?.in && !currentType?.out;
            const currentIsOutgoing = currentType?.out && !currentType?.in;
            const currentIsBoth = currentType?.in && currentType?.out;
            
            const prevIsIncoming = prevType?.in && !prevType?.out;
            const prevIsOutgoing = prevType?.out && !prevType?.in;
            const prevIsBoth = prevType?.in && prevType?.out;
            
            // Reset fee coin regardless of direction change
            handleChange('feeCoin', '');
            handleChange('feeTicker', '');
            
            // If direction changes, reset the relevant fields
            if (
                // If changing from incoming-only to outgoing-only or vice versa
                (currentIsIncoming && prevIsOutgoing) || 
                (currentIsOutgoing && prevIsIncoming) ||
                // If changing from both to incoming-only or outgoing-only
                (prevIsBoth && (currentIsIncoming || currentIsOutgoing)) ||
                // If changing from incoming-only or outgoing-only to both
                ((prevIsIncoming || prevIsOutgoing) && currentIsBoth)
            ) {
                handleChange('buyCoin', '');
                handleChange('buyValueInFiat', 0);
                handleChange('buyPricePerUnit', 0);
                handleChange('buyTicker', '');
                handleChange('sellCoin', '');
                handleChange('sellValueInFiat', 0);
                handleChange('sellPricePerUnit', 0);
                handleChange('sellTicker', '');
            }

            prevTypeIdRef.current = typeId;
            return;
        }
        if (prevTypeIdRef.current === undefined) {
            prevTypeIdRef.current = typeId;
        }

        let action: 'set' | 'clear' | 'ignore' = 'ignore';
        let newFeeCoin: string | null | undefined = undefined;
        let newFeeTicker: string | null | undefined = undefined;

        // Get the transaction type
        const transactionType = transactionTypesWithTransfer.find(t => t.value === typeId);
        const isOutgoingOnly = transactionType?.out && !transactionType?.in;
        const isIncomingOnly = transactionType?.in && !transactionType?.out;
        const isTransfer = transactionType?.value === TransactionTypeValue.Transfer;

        // For transfer transactions, fee coin should match buy coin
        if (isTransfer && buyCoin) {
            action = 'set';
            newFeeCoin = buyCoin;
            newFeeTicker = buyTicker || buyCoin;
        }
        // For outgoing-only transactions, fee coin should match sell coin
        else if (isOutgoingOnly && sellCoin) {
            action = 'set';
            newFeeCoin = sellCoin;
            newFeeTicker = sellTicker || sellCoin;
            
            // For outgoing-only transactions, also copy the price from sell to fee
            if (formValues.sellPricePerUnit && formValues.sellPricePerUnit > 0) {
                handleChange('feePricePerUnit', formValues.sellPricePerUnit);
                
                // If there's a fee amount, calculate the value
                if (formValues.fee) {
                    const feeValueInFiat = formValues.fee * formValues.sellPricePerUnit;
                    handleChange('feeValueInFiat', feeValueInFiat);
                }
            }
        }
        // For incoming-only transactions, fee coin should match buy coin
        else if (isIncomingOnly && buyCoin) {
            action = 'set';
            newFeeCoin = buyCoin;
            newFeeTicker = buyTicker || buyCoin;
        }
        // For other transaction types
        else if (feeAmount <= 0) {
            if (currentFeeCoin || currentFeeTicker) {
                action = 'clear';
            }
        } else {
            if (buyCoin && !sellCoin) {
                action = 'set';
                newFeeCoin = buyCoin;
                newFeeTicker = buyTicker || buyCoin;
            } else if (!buyCoin && sellCoin) {
                action = 'set';
                newFeeCoin = sellCoin;
                newFeeTicker = sellTicker || sellCoin;
            } else if (buyCoin && sellCoin) {
                if (buyCoin === sellCoin) {
                    action = 'set';
                    newFeeCoin = buyCoin;
                    newFeeTicker = buyTicker || buyCoin;
                } else {
                    if (typeId === TransactionTypeValue.Trade || typeId === TransactionTypeValue.SwapWrapNonTaxable) {
                        action = 'ignore';
                    } else {
                        action = 'clear';
                    }
                }
            } else {
                if (currentFeeCoin || currentFeeTicker) {
                    action = 'clear';
                }
            }
        }

        // Final Validation: Ensure selected feeCoin is still valid
        if (action !== 'clear' && currentFeeCoin) {
            const isBuyCoinOption = buyCoin && currentFeeCoin === buyCoin;
            const isSellCoinOption = sellCoin && currentFeeCoin === sellCoin;

            if (!isBuyCoinOption && !isSellCoinOption) {
                action = 'clear';
            }
        }

        if (action === 'set') {
            if (newFeeCoin !== currentFeeCoin || newFeeTicker !== currentFeeTicker) {
                handleChange('feeCoin', newFeeCoin);
                handleChange('feeTicker', newFeeTicker);
            }
        } else if (action === 'clear') {
            if (currentFeeCoin || currentFeeTicker) {
                handleChange('feeCoin', '');
                handleChange('feeTicker', '');
            }
        }
    }, [
        formValues.buyCoin,
        formValues.sellCoin,
        formValues.fee,
        formValues.typeId,
        formValues.feeCoin,
        formValues.buyTicker,
        formValues.sellTicker,
        formValues.feeTicker,
        handleChange,
        transactionTypesWithTransfer
    ]);

    const {
        data: liquidityPools = [],
        isLoading: isLiquidityPoolLoading,
    } = useQuery({
        queryKey: ['liquidityPools'],
        queryFn: transactionService.getLiquidityPools,
        staleTime: CACHE_CONSTANTS.MEDIUM_DATA_STALE_TIME,
        enabled: showLiquidityPool,
    });

    const fetchHistoricValue = async (type: 'buy' | 'sell' | 'fee') => {
        try {
            const coin = formValues[`${type}Coin`] || '';
            const date = formValues.date || 0;
            const amount = Number(formValues[type]) || 0;

            const result = await userService.getHistoricCoinValueInUserCurrency(coin, date, amount);
            if (typeof result.value !== 'number') {
                throw new Error('Invalid data format received from API');
            }

            const { value } = result;
            const price = amount === 0 ? 0 : value / amount;
            handleChange(`${type}ValueInFiat`, value);
            handleChange(`${type}PricePerUnit`, price);
            setInputValues(currentValues => ({
                ...currentValues,
                [`${type}ValueInFiat`]: value.toString().replace('.', ','),
                [`${type}PricePerUnit`]: price.toString().replace('.', ','),
            }));

            return result;
        } catch (error) {
            console.error(`Error fetching ${type} price:`, error);
            return { value: 0, price: 0 };
        }
    };

    const buyQuery = useQuery({
        queryKey: ['buyPrice', formValues.buyCoin, formValues.buy, formValues.date],
        queryFn: () => fetchHistoricValue('buy'),
        enabled: buyEnabled,
    });

    const sellQuery = useQuery({
        queryKey: ['sellPrice', formValues.sellCoin, formValues.sell, formValues.date],
        queryFn: () => fetchHistoricValue('sell'),
        enabled: sellEnabled,
    });

    const feeQuery = useQuery({
        queryKey: ['feePrice', formValues.feeCoin, formValues.fee, formValues.date],
        queryFn: () => fetchHistoricValue('fee'),
        enabled: feeEnabled,
    });

    useEffect(() => {
        setIsUpdatingPrices(() => ({
            buy: buyQuery.isLoading,
            sell: sellQuery.isLoading,
            fee: feeQuery.isLoading,
        }));
    }, [buyQuery.isLoading, sellQuery.isLoading, feeQuery.isLoading]);

    const formatInput = (field: string, value: number | undefined): string => {
        if (inputValues[field] !== undefined) {
            return inputValues[field];
        }

        if (value === undefined || value === null || value === 0) return '';

        if (field.includes('Value') && field.includes('Fiat')) {
            const str = value.toString();
            if (str.endsWith('.0')) return str.slice(0, -2);
            const formatted = str.replace('.', ',');
            return formatted.startsWith(',') ? '0' + formatted : formatted;
        } else if (field.includes('fee') || field.includes('buy') || field.includes('sell')) {
            const str = value.toString();
            if (str.endsWith('.0')) return str.slice(0, -2);
            const formatted = str.replace('.', ',');
            return formatted.startsWith(',') ? '0' + formatted : formatted;
        }

        const str = value.toString();
        if (str.endsWith('.0')) return str.slice(0, -2);
        const formatted = str.replace('.', ',');
        return formatted.startsWith(',') ? '0' + formatted : formatted;
    };

    const handleDateChange = (newDate: Date | null) => {
        if (newDate) {
            const currentDate = new Date(formValues.date ? formValues.date * 1000 : Date.now());
            const hours = currentDate.getHours();
            const minutes = currentDate.getMinutes();
            const seconds = currentDate.getSeconds();

            newDate.setHours(hours, minutes, seconds);
            const timestamp = Math.floor(newDate.getTime() / 1000);
            handleChange('date', timestamp);
        }
    };

    const handleTimeChange = (newTime: Date | null) => {
        if (!newTime) return;

        const currentDate = formValues.date ? new Date(formValues.date * 1000) : new Date();
        currentDate.setHours(newTime.getHours());
        currentDate.setMinutes(newTime.getMinutes());
        currentDate.setSeconds(newTime.getSeconds());

        handleChange('date', Math.floor(currentDate.getTime() / 1000));
    };

    const handleAccountChange = (value: string | null) => handleChange('account', extractValue(value));

    const handleAccountIncomingChange = (value: string | null) => handleChange('accountIncoming', extractValue(value));

    const handleCoinChange = (field: 'buyCoin' | 'sellCoin' | 'feeCoin', value: CoinWithSymbol | null | string) => {
        let tickerField: keyof Transaction | null = null;
        switch (field) {
            case 'buyCoin':
                tickerField = 'buyTicker';
                break;
            case 'sellCoin':
                tickerField = 'sellTicker';
                break;
            case 'feeCoin':
                tickerField = 'feeTicker';
                break;
            default:
                tickerField = null;
        }
        if (value === null) {
            handleChange(field, '');
            if (tickerField) handleChange(tickerField, '');
        } else if (typeof value === 'string') {
            handleChange(field, value);
            if (tickerField) handleChange(tickerField, value);
        } else {
            handleChange(field, value.ticker);
            if (tickerField) handleChange(tickerField, value.symbol);
        }
    };

    const handleAmountChange = (type: 'buy' | 'sell' | 'fee') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value;
        if (isValidDecimalInput(rawValue)) {
            const cleanValue = formatDecimalInput(rawValue);
            setInputValues(currentValues => ({ ...currentValues, [type]: cleanValue }));

            const value = parseDecimalInput(cleanValue);
            if (!isNaN(value)) {
                handleChange(type, value);
                const price = Number(formValues[`${type}PricePerUnit`]) || 0;
                const valueInFiat = value * price;
                handleChange(`${type}ValueInFiat`, valueInFiat);
                setInputValues(currentValues => ({
                    ...currentValues,
                    [`${type}ValueInFiat`]: valueInFiat.toString().replace('.', ','),
                }));
            }
        }
    };

    const handleBuyAmountChange = handleAmountChange('buy');

    const handlePriceChange = (type: 'buy' | 'sell' | 'fee') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value;
        if (isValidDecimalInput(rawValue)) {
            const cleanValue = formatDecimalInput(rawValue);
            setInputValues(currentValues => ({ ...currentValues, [`${type}PricePerUnit`]: cleanValue }));

            const value = parseDecimalInput(cleanValue);
            handleChange(`${type}PricePerUnit`, value);

            const amount = Number(formValues[type]) || 0;
            const valueInFiat = amount * value;
            handleChange(`${type}ValueInFiat`, valueInFiat);
            setInputValues(currentValues => ({
                ...currentValues,
                [`${type}ValueInFiat`]: valueInFiat.toString().replace('.', ','),
            }));
        }
    };

    const handleValueChange = (type: 'buy' | 'sell' | 'fee') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value;
        if (isValidDecimalInput(rawValue)) {
            const cleanValue = formatDecimalInput(rawValue);
            setInputValues(currentValues => ({ ...currentValues, [`${type}ValueInFiat`]: cleanValue }));

            const valueInFiat = parseDecimalInput(cleanValue);
            handleChange(`${type}ValueInFiat`, valueInFiat);

            const amount = Number(formValues[type]) || 0;
            const pricePerUnit = amount === 0 ? 0 : valueInFiat / amount;
            handleChange(`${type}PricePerUnit`, pricePerUnit);
            setInputValues(currentValues => ({
                ...currentValues,
                [`${type}PricePerUnit`]: pricePerUnit.toString().replace('.', ','),
            }));
        }
    };

    const handleBuyPriceChange = handlePriceChange('buy');
    const handleBuyValueChange = handleValueChange('buy');
    const handleSellPriceChange = handlePriceChange('sell');
    const handleSellValueChange = handleValueChange('sell');

    const handleBuyCoinChange = (value: CoinWithSymbol | null | string) => {
        handleCoinChange('buyCoin', value);
        if (!value) {
            handleChange('buyValueInFiat', 0);
            handleChange('buyPricePerUnit', 0);
        }
    };

    const handleSellAmountChange = handleAmountChange('sell');

    const handleSellCoinChange = (value: CoinWithSymbol | null | string) => {
        handleCoinChange('sellCoin', value);
        if (!value) {
            handleChange('sellValueInFiat', 0);
            handleChange('sellPricePerUnit', 0);
        }
    };

    const handleFeeAmountChange = handleAmountChange('fee');
    const handleFeePriceChange = handlePriceChange('fee');
    const handleFeeValueChange = handleValueChange('fee');

    const handleFeeCoinChange = (value: CoinWithSymbol | null | string) => {
        handleCoinChange('feeCoin', value);
        if (!value) {
            handleChange('feeValueInFiat', 0);
            handleChange('feePricePerUnit', 0);
        }
    };

    const handleBuyPoolChange = (value: string | null) => handleChange('buyPoolName', extractValue(value));
    const handleSellPoolChange = (value: string | null) => handleChange('sellPoolName', extractValue(value));

    const handleCommentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleChange('comment', e.target.value);
    };

    return {
        formatInput,
        handleBuyAmountChange,
        handleBuyPriceChange,
        handleBuyValueChange,
        handleBuyCoinChange,
        handleSellAmountChange,
        handleSellPriceChange,
        handleSellValueChange,
        handleSellCoinChange,
        handleDateChange,
        handleTimeChange,
        handleAccountChange,
        handleAccountIncomingChange,
        handleFeeCoinChange,
        handleFeeAmountChange,
        handleFeePriceChange,
        handleFeeValueChange,
        handleCommentChange,
        handleBuyPoolChange,
        handleSellPoolChange,
        isUpdatingPrices,
        liquidityPools,
        showLiquidityPool,
        isLiquidityPoolLoading,
    };
};
